import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import get_action_settings_api_call from '../../../lib/api/actions/get-action-settings';
import update_action_settings_api_call from '../../../lib/api/actions/update-action-settings';
import { ShopActionSettings } from '../../../lib/api/actions/action-types';
import { setMessage } from '../../../store/reducers/appSlice';
import '../actions.scss';

const ActionSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [settings, setSettings] = useState<ShopActionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [monthlyBudgetCap, setMonthlyBudgetCap] = useState<string>('');
  const [notificationEmail, setNotificationEmail] = useState<string>('');
  const [notificationWebhook, setNotificationWebhook] = useState<string>('');
  const [requireApprovalAbove, setRequireApprovalAbove] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await get_action_settings_api_call();
      if (result) {
        setSettings(result);
        setMonthlyBudgetCap(
          result.monthly_budget_cap !== null
            ? (result.monthly_budget_cap / 100).toFixed(2)
            : ''
        );
        setNotificationEmail(result.notification_email || '');
        setNotificationWebhook(result.notification_webhook || '');
        setRequireApprovalAbove(
          result.require_approval_above !== null
            ? (result.require_approval_above / 100).toFixed(2)
            : ''
        );
        setIsActive(result.is_active);
      }
    } catch (error) {
      console.error('Failed to load action settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await update_action_settings_api_call({
        monthly_budget_cap: monthlyBudgetCap
          ? Math.round(parseFloat(monthlyBudgetCap) * 100)
          : null,
        notification_email: notificationEmail || null,
        notification_webhook: notificationWebhook || null,
        require_approval_above: requireApprovalAbove
          ? Math.round(parseFloat(requireApprovalAbove) * 100)
          : null,
        is_active: isActive,
      });

      if (result) {
        setSettings(result);
        dispatch(
          setMessage({
            message: t('SettingsSavedSuccessfully'),
            messageType: 'success',
          })
        );
      } else {
        dispatch(
          setMessage({
            message: t('FailedToSaveSettings'),
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Failed to save action settings:', error);
      dispatch(
        setMessage({
          message: t('FailedToSaveSettings'),
          messageType: 'error',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const getBudgetPercentage = (): number => {
    if (!settings || !settings.monthly_budget_cap) return 0;
    return Math.min(
      100,
      (settings.monthly_budget_used / settings.monthly_budget_cap) * 100
    );
  };

  const getBudgetBarClass = (): string => {
    const pct = getBudgetPercentage();
    if (pct >= 90) return 'action-settings-form__progress-bar-fill--danger';
    if (pct >= 70) return 'action-settings-form__progress-bar-fill--warning';
    return '';
  };

  if (loading) {
    return (
      <SubPageInnerContentHolder
        title={t('ActionSettings')}
        backBtnLink={HOME_ROUTE}
      >
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingSettings')} />
        </div>
      </SubPageInnerContentHolder>
    );
  }

  return (
    <SubPageInnerContentHolder
      title={t('ActionSettings')}
      backBtnLink={HOME_ROUTE}
    >
      <div className='action-settings-form'>
        {/* Monthly Budget Cap */}
        <div className='action-settings-form__field'>
          <p className='action-settings-form__field-label'>
            {t('MonthlyBudgetCap')}
          </p>
          <p className='action-settings-form__field-description'>
            {t('MaximumTotalBudgetChangesAllowedPerMonth')}
          </p>
          <div className='action-settings-form__field-input-wrapper'>
            <span className='action-settings-form__field-input-wrapper--prefix'>
              $
            </span>
            <input
              type='number'
              value={monthlyBudgetCap}
              onChange={(e) => setMonthlyBudgetCap(e.target.value)}
              placeholder='0.00'
              min='0'
              step='0.01'
            />
          </div>
        </div>

        {/* Monthly Budget Used */}
        {settings && settings.monthly_budget_cap !== null && (
          <div className='action-settings-form__field'>
            <p className='action-settings-form__field-label'>
              {t('MonthlyBudgetUsed')}
            </p>
            <p className='action-settings-form__field-description'>
              ${(settings.monthly_budget_used / 100).toFixed(2)} / $
              {(settings.monthly_budget_cap / 100).toFixed(2)} (
              {getBudgetPercentage().toFixed(1)}%)
            </p>
            <div className='action-settings-form__progress-bar'>
              <div
                className={`action-settings-form__progress-bar-fill ${getBudgetBarClass()}`}
                style={{ width: `${getBudgetPercentage()}%` }}
              />
            </div>
          </div>
        )}

        {/* Notification Email */}
        <div className='action-settings-form__field'>
          <p className='action-settings-form__field-label'>
            {t('NotificationEmail')}
          </p>
          <p className='action-settings-form__field-description'>
            {t('ReceiveEmailNotificationsForActionProposals')}
          </p>
          <input
            type='email'
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            placeholder='you@example.com'
          />
        </div>

        {/* Notification Webhook URL */}
        <div className='action-settings-form__field'>
          <p className='action-settings-form__field-label'>
            {t('NotificationWebhookURL')}
          </p>
          <p className='action-settings-form__field-description'>
            {t('ReceiveWebhookNotificationsForActionEvents')}
          </p>
          <input
            type='url'
            value={notificationWebhook}
            onChange={(e) => setNotificationWebhook(e.target.value)}
            placeholder='https://your-webhook-url.com/actions'
          />
        </div>

        {/* Require Manual Approval Above */}
        <div className='action-settings-form__field'>
          <p className='action-settings-form__field-label'>
            {t('RequireManualApprovalAbove')}
          </p>
          <p className='action-settings-form__field-description'>
            {t('ActionsAboveThisAmountRequireManualApproval')}
          </p>
          <div className='action-settings-form__field-input-wrapper'>
            <span className='action-settings-form__field-input-wrapper--prefix'>
              $
            </span>
            <input
              type='number'
              value={requireApprovalAbove}
              onChange={(e) => setRequireApprovalAbove(e.target.value)}
              placeholder='0.00'
              min='0'
              step='0.01'
            />
          </div>
        </div>

        {/* Auto-Approval Active */}
        <div className='action-settings-form__field'>
          <p className='action-settings-form__field-label'>
            {t('AutoApprovalActive')}
          </p>
          <div className='action-settings-form__toggle-field'>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: '1.4rem',
                  fontFamily: 'var(--main-font-primary)',
                  color: 'var(--main-text-color)',
                  fontWeight: 500,
                }}
              >
                {isActive
                  ? t('ActionsMiddlewareIsActive')
                  : t('ActionsMiddlewareIsInactive')}
              </p>
              <p
                style={{
                  margin: '0.3rem 0 0 0',
                  fontSize: '1.2rem',
                  fontFamily: 'var(--main-font-secondary)',
                  color: 'var(--sidebar-sublink-gray-text-color)',
                }}
              >
                {t('EnableOrDisableAutomatedActionProcessing')}
              </p>
            </div>
            <ToggleSwitch
              isOn={isActive}
              onClick={(checked: boolean) => setIsActive(checked)}
              id='action-auto-approval-toggle'
            />
          </div>
        </div>

        {/* Save Button */}
        <div className='action-settings-form__actions'>
          <MainSquareButton
            value={t('SaveSettings')}
            type='button'
            onClick={handleSave}
            disabled={saving}
            loading={saving}
          />
        </div>
      </div>
    </SubPageInnerContentHolder>
  );
};

export default ActionSettings;
